// src/components/estimation/detailed/steps/form/formItem.tsx
import React, { useState, useRef, useEffect } from 'react';
import { Info, X, Calculator, Sparkles } from 'lucide-react';
import { ItemDetails } from '../../../../../types/estimation-items';
import { ITEM_CALCULATOR_MAP, CalculatorType } from '../../../../../data/calculators';
import { SuggestionStrength } from '../../../../../data/dependencies';

// ─── Types ────────────────────────────────────────────────────────────────────

interface FormItemProps {
  label: string;
  value?: {
    selected: boolean;
    value?: number;
    quantity?: number;
  };
  onChange: (value: { selected: boolean; value?: number; quantity?: number }) => void;
  withDistance?: boolean;
  withSurface?: boolean;
  withQuantity?: boolean;
  withLength?: boolean;
  withVolume?: boolean;
  withCapacity?: boolean;
  withCustomAmount?: boolean;
  withPercentage?: boolean;
  itemDetails?: ItemDetails;
  itemKey?: string;
  suggestion?: { strength: SuggestionStrength; reason: string };
}

interface TooltipPos {
  top?: number;
  bottom?: number;
  left: number;
}

// ─── Shared helpers ───────────────────────────────────────────────────────────

const hasTooltipContent = (d: ItemDetails): boolean =>
  !!(d.note || d.performance || d.workIncluded?.length);

const isMobile = () => window.innerWidth < 768;

const computePanelPos = (iconEl: HTMLElement, panelMinH: number): TooltipPos => {
  const rect = iconEl.getBoundingClientRect();
  const spaceBelow = window.innerHeight - rect.bottom;
  const left = Math.min(Math.max(rect.left - 8, 8), window.innerWidth - 316);
  if (spaceBelow < panelMinH) {
    return { bottom: window.innerHeight - rect.top + 8, left };
  }
  return { top: rect.bottom + 8, left };
};

// ─── FieldRow — input row for calculator ──────────────────────────────────────

interface FieldRowProps {
  label: string;
  fieldValue: number;
  onFieldChange: (v: number) => void;
  step?: number;
  suffix?: string;
}

const FieldRow: React.FC<FieldRowProps> = ({ label, fieldValue, onFieldChange, step = 0.1, suffix }) => (
  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginBottom: 6 }}>
    <span style={{ fontSize: 11, color: '#7C6FA0', flexShrink: 0, lineHeight: 1.3 }}>{label}</span>
    <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0 }}>
      <input
        type="number"
        value={fieldValue || ''}
        onChange={(e) => onFieldChange(Number(e.target.value) || 0)}
        step={step}
        min={0}
        style={{
          border: '0.5px solid var(--color-border)',
          borderRadius: 'var(--radius-sm)',
          padding: '5px 8px',
          fontSize: 12,
          width: 70,
          textAlign: 'right',
          outline: 'none',
          background: 'white',
        }}
      />
      {suffix && (
        <span style={{ fontSize: 10, color: '#9CA3AF', whiteSpace: 'nowrap', minWidth: 44 }}>
          {suffix}
        </span>
      )}
    </div>
  </div>
);

// ─── ItemTooltip ──────────────────────────────────────────────────────────────

const ItemTooltip: React.FC<{ details: ItemDetails }> = ({ details }) => {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<TooltipPos>({ left: 0 });
  const iconRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const leaveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  if (!hasTooltipContent(details)) return null;

  const clearLeave = () => { if (leaveTimer.current) clearTimeout(leaveTimer.current); };
  const scheduleClose = () => { leaveTimer.current = setTimeout(() => setOpen(false), 120); };
  const openPanel = () => { if (iconRef.current) setPos(computePanelPos(iconRef.current, 200)); setOpen(true); };

  // eslint-disable-next-line react-hooks/rules-of-hooks
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (!panelRef.current?.contains(e.target as Node) && !iconRef.current?.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const sections: React.ReactNode[] = [];

  if (details.note) {
    sections.push(
      <p key="note" style={{ fontSize: 12, color: '#1E1148', lineHeight: 1.55 }}>{details.note}</p>
    );
  }
  if (details.performance) {
    if (sections.length > 0) sections.push(
      <hr key="sep-perf" style={{ border: 'none', borderTop: '0.5px solid var(--color-border)', margin: '8px 0' }} />
    );
    sections.push(
      <div key="perf">
        <p style={{ fontSize: 10, fontWeight: 500, color: '#7C6FA0', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 3 }}>
          Performance
        </p>
        <p style={{ fontSize: 12, color: '#1E1148', lineHeight: 1.55 }}>{details.performance}</p>
      </div>
    );
  }
  if (details.workIncluded?.length) {
    if (sections.length > 0) sections.push(
      <hr key="sep-work" style={{ border: 'none', borderTop: '0.5px solid var(--color-border)', margin: '8px 0' }} />
    );
    sections.push(
      <div key="work">
        <p style={{ fontSize: 10, fontWeight: 500, color: '#7C6FA0', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4 }}>
          Inclus
        </p>
        {details.workIncluded.slice(0, 3).map((w, i) => (
          <p key={i} style={{ fontSize: 12, color: '#1E1148', lineHeight: 1.55 }}>
            <span style={{ color: 'rgb(var(--color-primary))' }}>·</span>{' '}{w}
          </p>
        ))}
      </div>
    );
  }

  const mobile = isMobile();

  return (
    <>
      <button
        ref={iconRef}
        type="button"
        aria-label="Aide contextuelle"
        onMouseEnter={() => { clearLeave(); if (!mobile) openPanel(); }}
        onMouseLeave={() => { if (!mobile) scheduleClose(); }}
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); open ? setOpen(false) : openPanel(); }}
        style={{ background: 'none', border: 'none', padding: 0, cursor: 'help', display: 'inline-flex', alignItems: 'center', flexShrink: 0, lineHeight: 0 }}
      >
        <Info
          size={14}
          style={{ color: open ? 'rgb(var(--color-primary))' : 'rgba(109,40,217,0.4)', transition: 'color 0.15s' }}
        />
      </button>

      {open && (
        <div
          ref={panelRef}
          onMouseEnter={() => { clearLeave(); }}
          onMouseLeave={() => { if (!mobile) scheduleClose(); }}
          style={{
            position: 'fixed', ...pos,
            background: 'white',
            border: '0.5px solid var(--color-border)',
            borderRadius: 'var(--radius-md)',
            boxShadow: 'var(--shadow-card)',
            padding: '12px 14px',
            maxWidth: 280, width: 280,
            zIndex: 100,
          }}
        >
          {mobile && (
            <button type="button" onClick={() => setOpen(false)}
              style={{ position: 'absolute', top: 8, right: 8, background: 'none', border: 'none', cursor: 'pointer', padding: 2, display: 'flex', alignItems: 'center' }}>
              <X size={12} color="#7C6FA0" />
            </button>
          )}
          {sections}
        </div>
      )}
    </>
  );
};

// ─── SuggestionBadge ─────────────────────────────────────────────────────────

interface SuggestionBadgeProps {
  strength: SuggestionStrength;
  reason: string;
}

const SuggestionBadge: React.FC<SuggestionBadgeProps> = ({ strength, reason }) => {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<TooltipPos>({ left: 0 });
  const badgeRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  const isRecommended = strength === 'recommended';
  const color       = isRecommended ? '#C2511F' : '#7B3FA0';
  const iconColor   = isRecommended ? 'rgba(194,81,31,0.7)' : 'rgba(109,40,217,0.5)';

  const openPanel = () => {
    if (badgeRef.current) setPos(computePanelPos(badgeRef.current, 120));
    setOpen(true);
  };

  // eslint-disable-next-line react-hooks/rules-of-hooks
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (
        !panelRef.current?.contains(e.target as Node) &&
        !badgeRef.current?.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  return (
    <>
      <button
        ref={badgeRef}
        type="button"
        aria-label="Pourquoi cette suggestion ?"
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); open ? setOpen(false) : openPanel(); }}
        style={{
          background: 'none', border: 'none', padding: 0, cursor: 'pointer',
          display: 'inline-flex', alignItems: 'center', gap: 3, flexShrink: 0,
          lineHeight: 0, marginLeft: 6,
        }}
      >
        <Sparkles size={12} style={{ color: iconColor, flexShrink: 0 }} />
        <span style={{ fontSize: 10, color, fontStyle: 'italic', whiteSpace: 'nowrap' }}>
          Suggéré
        </span>
      </button>

      {open && (
        <div
          ref={panelRef}
          style={{
            position: 'fixed', ...pos,
            background: 'white',
            border: '0.5px solid var(--color-border)',
            borderRadius: 'var(--radius-md)',
            boxShadow: 'var(--shadow-card)',
            padding: '12px 14px',
            maxWidth: 260, width: 260,
            zIndex: 100,
          }}
        >
          <p style={{ fontSize: 11, fontWeight: 500, color: '#3D1A78', marginBottom: 8 }}>
            Pourquoi cette suggestion ?
          </p>
          <p style={{ fontSize: 12, color: '#1E1148', lineHeight: 1.55 }}>
            {reason}
          </p>
        </div>
      )}
    </>
  );
};

// ─── ItemCalculator ───────────────────────────────────────────────────────────

interface ItemCalculatorProps {
  itemKey: string;
  value: { selected: boolean; value?: number; quantity?: number };
  onChange: (v: { selected: boolean; value?: number; quantity?: number }) => void;
}

type CalcFields = {
  longueur: number; largeur: number; hauteur: number;
  nbFen: number; nbPortes: number;
  f1: number; f2: number; f3: number; f4: number;
  profondeur: number;
  byPerimeter: boolean;
};

const PANEL_TITLES: Record<CalculatorType, string> = {
  FLOOR:            'Calculer la surface au sol',
  CEILING:          'Calculer la surface plafond',
  WALL:             'Calculer la surface murale',
  FACADE:           'Calculer la surface de façade',
  PARTITION:        'Calculer la surface de cloison',
  LINEAR_SIMPLE:    'Calculer la longueur',
  LINEAR_PERIMETER: 'Calculer la longueur',
  VOLUME:           'Calculer le volume',
};

const CALC_UNIT: Record<CalculatorType, string> = {
  FLOOR: 'm²', CEILING: 'm²', WALL: 'm²', FACADE: 'm²', PARTITION: 'm²',
  LINEAR_SIMPLE: 'ml', LINEAR_PERIMETER: 'ml',
  VOLUME: 'm³',
};

const ItemCalculator: React.FC<ItemCalculatorProps> = ({ itemKey, value, onChange }) => {
  const calcType = ITEM_CALCULATOR_MAP[itemKey];
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<TooltipPos>({ left: 0 });
  const iconRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const [fields, setFields] = useState<CalcFields>({
    longueur: 0, largeur: 0, hauteur: 2.5,
    nbFen: 0, nbPortes: 0,
    f1: 0, f2: 0, f3: 0, f4: 0,
    profondeur: 0,
    byPerimeter: false,
  });

  if (!calcType) return null;

  const setField = (k: keyof Omit<CalcFields, 'byPerimeter'>, v: number) =>
    setFields(prev => ({ ...prev, [k]: v }));
  const togglePerimeter = () =>
    setFields(prev => ({ ...prev, byPerimeter: !prev.byPerimeter }));

  const openPanel = () => {
    if (iconRef.current) setPos(computePanelPos(iconRef.current, 280));
    setOpen(true);
  };

  // eslint-disable-next-line react-hooks/rules-of-hooks
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (!panelRef.current?.contains(e.target as Node) && !iconRef.current?.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const compute = (): number | null => {
    const { longueur, largeur, hauteur, nbFen, nbPortes, f1, f2, f3, f4, profondeur, byPerimeter } = fields;
    const round2 = (n: number) => Math.round(n * 100) / 100;
    switch (calcType) {
      case 'FLOOR':
      case 'CEILING':
        if (!longueur || !largeur) return null;
        return round2(longueur * largeur);
      case 'WALL':
        if (!longueur || !largeur || !hauteur) return null;
        return round2(2 * (longueur + largeur) * hauteur - nbFen * 1.5 - nbPortes * 2);
      case 'FACADE':
        if (!hauteur || !f1) return null;
        return round2((f1 + f2 + f3 + f4) * hauteur - nbFen * 1.5 - nbPortes * 2.5);
      case 'PARTITION':
        if (!longueur || !hauteur) return null;
        return round2(longueur * hauteur);
      case 'LINEAR_SIMPLE':
        if (!longueur) return null;
        return round2(longueur);
      case 'LINEAR_PERIMETER':
        if (byPerimeter) {
          if (!longueur || !largeur) return null;
          return round2(2 * (longueur + largeur) - nbPortes * 0.9);
        }
        if (!longueur) return null;
        return round2(longueur);
      case 'VOLUME':
        if (!longueur || !largeur || !profondeur) return null;
        return round2(longueur * largeur * profondeur);
    }
  };

  const result = compute();
  const isValid = result !== null && result > 0 && !isNaN(result);
  const isError = result !== null && (result <= 0 || isNaN(result));
  const mobile = isMobile();

  return (
    <>
      <button
        ref={iconRef}
        type="button"
        aria-label="Calculer la surface"
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); open ? setOpen(false) : openPanel(); }}
        style={{ background: 'none', border: 'none', padding: 0, cursor: 'help', display: 'inline-flex', alignItems: 'center', flexShrink: 0, lineHeight: 0 }}
      >
        <Calculator
          size={14}
          style={{ color: open ? 'rgb(var(--color-primary))' : 'rgba(109,40,217,0.35)', transition: 'color 0.15s' }}
        />
      </button>

      {open && (
        <div
          ref={panelRef}
          style={{
            position: 'fixed', ...pos,
            background: 'white',
            border: '0.5px solid var(--color-border)',
            borderRadius: 'var(--radius-md)',
            boxShadow: '0 4px 20px rgba(61,26,120,0.12)',
            padding: '14px 16px',
            maxWidth: 300, width: 300,
            zIndex: 100,
          }}
        >
          {mobile && (
            <button type="button" onClick={() => setOpen(false)}
              style={{ position: 'absolute', top: 8, right: 8, background: 'none', border: 'none', cursor: 'pointer', padding: 2, display: 'flex', alignItems: 'center' }}>
              <X size={12} color="#7C6FA0" />
            </button>
          )}

          <p style={{ fontSize: 12, fontWeight: 500, color: '#1E1148', marginBottom: 12 }}>
            {PANEL_TITLES[calcType]}
          </p>

          {/* ── FLOOR / CEILING ── */}
          {(calcType === 'FLOOR' || calcType === 'CEILING') && (
            <>
              <FieldRow label="Longueur (m)" fieldValue={fields.longueur} onFieldChange={(v) => setField('longueur', v)} />
              <FieldRow label="Largeur (m)"  fieldValue={fields.largeur}  onFieldChange={(v) => setField('largeur', v)} />
            </>
          )}

          {/* ── WALL ── */}
          {calcType === 'WALL' && (
            <>
              <FieldRow label="Longueur pièce (m)"      fieldValue={fields.longueur}  onFieldChange={(v) => setField('longueur', v)} />
              <FieldRow label="Largeur pièce (m)"       fieldValue={fields.largeur}   onFieldChange={(v) => setField('largeur', v)} />
              <FieldRow label="Hauteur sous plafond (m)" fieldValue={fields.hauteur}  onFieldChange={(v) => setField('hauteur', v)} />
              <FieldRow label="Nombre de fenêtres"      fieldValue={fields.nbFen}     onFieldChange={(v) => setField('nbFen', v)}    step={1} suffix="× 1.5 m²" />
              <FieldRow label="Nombre de portes"        fieldValue={fields.nbPortes}  onFieldChange={(v) => setField('nbPortes', v)} step={1} suffix="× 2.0 m²" />
            </>
          )}

          {/* ── FACADE ── */}
          {calcType === 'FACADE' && (
            <>
              <FieldRow label="Hauteur (m)"              fieldValue={fields.hauteur}  onFieldChange={(v) => setField('hauteur', v)} />
              <FieldRow label="Façade 1 (m)"             fieldValue={fields.f1}       onFieldChange={(v) => setField('f1', v)} />
              <FieldRow label="Façade 2 (m)"             fieldValue={fields.f2}       onFieldChange={(v) => setField('f2', v)} />
              <FieldRow label="Façade 3 (m, optionnel)"  fieldValue={fields.f3}       onFieldChange={(v) => setField('f3', v)} />
              <FieldRow label="Façade 4 (m, optionnel)"  fieldValue={fields.f4}       onFieldChange={(v) => setField('f4', v)} />
              <FieldRow label="Fenêtres"                 fieldValue={fields.nbFen}    onFieldChange={(v) => setField('nbFen', v)}    step={1} suffix="× 1.5 m²" />
              <FieldRow label="Portes / baies vitrées"   fieldValue={fields.nbPortes} onFieldChange={(v) => setField('nbPortes', v)} step={1} suffix="× 2.5 m²" />
            </>
          )}

          {/* ── PARTITION ── */}
          {calcType === 'PARTITION' && (
            <>
              <FieldRow label="Longueur (m)" fieldValue={fields.longueur} onFieldChange={(v) => setField('longueur', v)} />
              <FieldRow label="Hauteur (m)"  fieldValue={fields.hauteur}  onFieldChange={(v) => setField('hauteur', v)} />
            </>
          )}

          {/* ── LINEAR_SIMPLE — longueur seule ── */}
          {calcType === 'LINEAR_SIMPLE' && (
            <FieldRow label="Longueur (m)" fieldValue={fields.longueur} onFieldChange={(v) => setField('longueur', v)} />
          )}

          {/* ── LINEAR_PERIMETER — longueur + option périmètre ── */}
          {calcType === 'LINEAR_PERIMETER' && (
            <>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 10, cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={fields.byPerimeter}
                  onChange={togglePerimeter}
                  style={{ width: 13, height: 13, cursor: 'pointer', accentColor: 'rgb(var(--color-primary))' }}
                />
                <span style={{ fontSize: 11, color: '#7C6FA0' }}>Calculer par périmètre de pièce</span>
              </label>

              {fields.byPerimeter ? (
                <>
                  <FieldRow label="Longueur pièce (m)" fieldValue={fields.longueur} onFieldChange={(v) => setField('longueur', v)} />
                  <FieldRow label="Largeur pièce (m)"  fieldValue={fields.largeur}  onFieldChange={(v) => setField('largeur', v)} />
                  <FieldRow label="Nb portes"           fieldValue={fields.nbPortes} onFieldChange={(v) => setField('nbPortes', v)} step={1} suffix="× 0.9 ml" />
                </>
              ) : (
                <FieldRow label="Longueur (m)" fieldValue={fields.longueur} onFieldChange={(v) => setField('longueur', v)} />
              )}
            </>
          )}

          {/* ── VOLUME ── */}
          {calcType === 'VOLUME' && (
            <>
              <FieldRow label="Longueur (m)"    fieldValue={fields.longueur}   onFieldChange={(v) => setField('longueur', v)} />
              <FieldRow label="Largeur (m)"     fieldValue={fields.largeur}    onFieldChange={(v) => setField('largeur', v)} />
              <FieldRow label="Profondeur (m)"  fieldValue={fields.profondeur} onFieldChange={(v) => setField('profondeur', v)} />
              <p style={{ fontSize: 10, color: '#7C6FA0', fontStyle: 'italic', marginTop: 4, marginBottom: 4, lineHeight: 1.4 }}>
                Règle pratique : 1 m² de démolition ≈ 0.15 à 0.30 m³ de gravats
              </p>
            </>
          )}

          {/* ── Result row ── */}
          <div style={{ borderTop: '0.5px solid var(--color-border)', marginTop: 8, paddingTop: 8, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
            {isValid ? (
              <>
                <span style={{ fontSize: 13, fontWeight: 500, color: '#1E1148' }}>
                  {result!.toFixed(2)} {CALC_UNIT[calcType]}
                </span>
                <button
                  type="button"
                  onClick={() => { onChange({ ...value, value: result!, quantity: result! }); setOpen(false); }}
                  style={{
                    background: 'var(--gradient-primary)',
                    color: 'white',
                    border: 'none',
                    borderRadius: 'var(--radius-sm)',
                    padding: '4px 10px',
                    fontSize: 11,
                    fontWeight: 500,
                    cursor: 'pointer',
                    whiteSpace: 'nowrap',
                  }}
                >
                  Utiliser
                </button>
              </>
            ) : isError ? (
              <span style={{ fontSize: 12, color: '#DC2626' }}>Vérifiez les valeurs</span>
            ) : (
              <span style={{ fontSize: 12, color: '#9CA3AF', fontStyle: 'italic' }}>Saisissez les dimensions</span>
            )}
          </div>
        </div>
      )}
    </>
  );
};

// ─── FormItem ─────────────────────────────────────────────────────────────────

export const FormItem: React.FC<FormItemProps> = ({
  label,
  value = { selected: false },
  onChange,
  withDistance,
  withSurface,
  withQuantity,
  withLength,
  withVolume,
  withCapacity,
  withCustomAmount,
  withPercentage,
  itemDetails,
  itemKey,
  suggestion,
}) => {

  const numericValue = value.value ?? value.quantity ?? 0;
  const showSuggestion = !!suggestion && !value.selected;

  const getUnit = () => {
    if (withDistance)    return 'm';
    if (withSurface)     return 'm²';
    if (withLength)      return 'ml';
    if (withVolume)      return 'm³';
    if (withCapacity)    return 'L';
    if (withQuantity)    return 'unité(s)';
    if (withCustomAmount)return '€';
    if (withPercentage)  return '%';
    return '';
  };

  const showCalculator = (withSurface || withLength || withVolume) && !!itemKey && !!ITEM_CALCULATOR_MAP[itemKey];

  const wrapperStyle: React.CSSProperties = showSuggestion ? {
    background: 'rgba(109,40,217,0.04)',
    borderLeft: '2px solid rgba(109,40,217,0.25)',
    borderRadius: 'var(--radius-sm)',
    paddingLeft: 8,
  } : {};

  return (
    <div className="space-y-2" style={wrapperStyle}>
      <label className="flex items-start space-x-3">
        <input
          type="checkbox"
          checked={value.selected}
          onChange={(e) => onChange({ ...value, selected: e.target.checked })}
          className="mt-1 h-4 w-4 rounded border-neutral-300 text-primary focus:ring-primary"
        />
        <span className="flex items-center gap-1.5" style={{ minWidth: 0, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 13, color: 'var(--color-text-primary)' }}>{label}</span>
          {itemDetails && hasTooltipContent(itemDetails) && (
            <ItemTooltip details={itemDetails} />
          )}
          {showSuggestion && (
            <SuggestionBadge strength={suggestion!.strength} reason={suggestion!.reason} />
          )}
        </span>
      </label>

      {value.selected && (withDistance || withSurface || withQuantity || withLength || withVolume || withCapacity) && (
        <div className="ml-8 flex items-center space-x-2">
          <input
            type="number"
            value={numericValue || ''}
            onChange={(e) => {
              const newValue = Number(e.target.value) || 0;
              onChange({ ...value, value: newValue, quantity: newValue });
            }}
            min="0"
            step={withSurface || withLength || withVolume || withDistance ? "0.1" : "1"}
            className="input w-32"
            style={{ fontSize: 13 }}
            placeholder={`Saisir en ${getUnit()}`}
          />
          <span style={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>{getUnit()}</span>
          {showCalculator && (
            <ItemCalculator itemKey={itemKey!} value={value} onChange={onChange} />
          )}
        </div>
      )}

      {value.selected && withPercentage && (
        <div className="ml-8 space-y-2">
          <div className="flex items-center space-x-2">
            <input
              type="number"
              value={numericValue || ''}
              onChange={(e) => {
                const newValue = Number(e.target.value) || 0;
                onChange({ ...value, value: newValue, quantity: newValue });
              }}
              min="0"
              max="100"
              step="0.5"
              className="input w-32"
              style={{ fontSize: 13 }}
              placeholder="Pourcentage"
            />
            <span style={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>%</span>
          </div>
          <p style={{ fontSize: 12, color: 'var(--color-text-secondary)', fontStyle: 'italic' }}>
            Ce montant sera calculé sur le total HT des travaux après application du coefficient régional
          </p>
        </div>
      )}

      {value.selected && withCustomAmount && (
        <div className="ml-8 flex items-center space-x-2">
          <input
            type="number"
            value={numericValue || ''}
            onChange={(e) => {
              const newValue = Number(e.target.value) || 0;
              onChange({ ...value, value: newValue, quantity: newValue });
            }}
            min="0"
            step="1"
            className="input w-32"
            style={{ fontSize: 13 }}
            placeholder="Montant en euros"
          />
          <span style={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>{getUnit()}</span>
        </div>
      )}

    </div>
  );
};

export default FormItem;
